import { keyframes } from '@emotion/react';

import type { Utility } from '../../../types/utility';
import { mqMin, BREAKPOINT } from '../../breakpoints';

// animation keyframes for carousel variants\
const mobileCarouselScroll = keyframes({
  from: { transfrom: 'translateX(0)' },
  to: { transform: 'translateX(calc(((150px * 6) + 120px) * -1))' },
});

const mobileCarouselScroll1 = keyframes({
  from: { transform: 'translateX(calc((150px * 6) + 120px))' },
  to: { transform: 'translateX(0)' },
});

const desktopCarouselScroll = keyframes({
  from: { transfrom: 'translateX(0)' },
  to: { transform: 'translateX(-100%)' },
});

const desktopCarouselScroll1 = keyframes({
  from: { transform: 'translateX(100%)' },
  to: { transform: 'translateX(0)' },
});

const baseVariantShared: Utility.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  listStyle: 'none',
  rowGap: 's64',
  columnGap: 's48',
  justifyContent: 'center',
  width: '100%',
  marginLeft: 0,
  '& > li': {
    flexBasis: 'calc(50% - 48px)',
    maxHeight: '104px',
    minWidth: '70px',
    display: 'flex',
    alignItems: 'center',
  },
  [mqMin.tabletSmall]: {
    columnGap: 's64',
    '& > li': {
      maxHeight: 'none',
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: 'calc(33% - 64px)',
    },
  },
  [mqMin.tabletLandscape]: {
    rowGap: 's80',
    columnGap: 's64',
  },
};

const baselayouts: Utility.VariantCSSKeys<LayoutVariantsType> = {
  default: baseVariantShared,
  oneLine: {
    ...baseVariantShared,
    [mqMin.tabletLandscape]: {
      flexWrap: 'nowrap',
      columnGap: 's80',
      '& > li': {
        // helps normalize particularly wide logos
        flexBasis: 'auto',
        maxWidth: '25%',
      },
    },
  },
  autoWrap: {
    ...baseVariantShared,
    [mqMin.tabletLandscape]: {
      columnGap: 's80',
      '& > li': {
        flexBasis: 'auto',
        maxWidth: '25%',
      },
    },
  },
  threeWrap: {
    ...baseVariantShared,
    [mqMin.tabletSmall]: {
      '& > li': {
        flexBasis: 'calc(33% - 64px)',
      },
    },
  },
  fourWrap: {
    ...baseVariantShared,
    [mqMin.tabletLandscape]: {
      columnGap: 's64',
      '& > li': {
        flexBasis: 'calc(25% - 64px)',
      },
    },
  },
  fiveWrap: {
    ...baseVariantShared,
    [mqMin.tabletLandscape]: {
      '& > li': {
        flexBasis: 'calc(20% - 64px)',
      },
    },
  },
  twoOneTwo: {
    listStyle: 'none',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 's64',
    '& li': {
      display: 'flex',
      alignItems: 'center',
    },
    '& > li:nth-child(3n+3)': {
      gridColumnStart: '1',
      gridColumnEnd: '3',
    },
  },
  mobileCarousel: {
    display: 'flex',
    flexWrap: 'nowrap',
    listStyle: 'none',
    gap: 's64',
    justifyContent: 'center',
    minWidth: BREAKPOINT.TABLET_LANDSCAPE,
    width: '100%',
    marginLeft: 0,
    paddingRight: 's64',
    [mqMin.tabletLandscape]: {
      flexWrap: 'wrap',
      paddingRight: 0,
    },
    '&:nth-of-type(1)': {
      animation: `${mobileCarouselScroll} 15s linear infinite`,
      [mqMin.tabletLandscape]: {
        animation: 'unset',
      },
    },
    '&:nth-of-type(2)': {
      animation: `${mobileCarouselScroll1} 15s linear infinite`,
      [mqMin.tabletLandscape]: {
        animation: 'unset',
        display: 'none',
      },
      position: 'absolute',
      top: 0,
      bottom: 0,
    },
    '& > li': {
      display: 'flex',
      alignItems: 'center',
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: 'calc(20% - 64px)',
    },
  },
  desktopCarousel: {
    display: 'block',
    whiteSpace: 'nowrap',
    listStyle: 'none',
    width: 'max-content',
    marginLeft: 0,
    '&:nth-of-type(1)': {
      animationName: desktopCarouselScroll,
      animationIterationCount: 'infinite',
      animationTimingFunction: 'linear',
    },
    '&:nth-of-type(2)': {
      animationName: desktopCarouselScroll1,
      animationIterationCount: 'infinite',
      animationTimingFunction: 'linear',
      position: 'absolute',
      top: 0,
      bottom: 0,
      margin: 0,
    },
    '& > li': {
      display: 'inline-block',
      margin: '0 45px',
      width: '150px',
      '& img': {
        verticalAlign: 'middle',
      },
    },
  },
  condensed: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 's40',
    listStyle: 'none',
    margin: 0,
    '& > li': {
      height: 'unset',
      '& img': {
        height: '28px',
        maxWidth: '170px',
        objectFit: 'contain',
      },
    },
    [mqMin.tabletLandscape]: {
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  },
};

const layout: Utility.VariantKeyModes<LayoutVariantsType> = {
  products: {
    chart: baselayouts,
    chartblog: baselayouts,
    co: baselayouts,
    edu: baselayouts,
    scale: baselayouts,
    spark: baselayouts,
  },
};

export type LayoutVariantsType =
  | 'default'
  | 'oneLine'
  | 'autoWrap'
  | 'threeWrap'
  | 'fourWrap'
  | 'fiveWrap'
  | 'twoOneTwo'
  | 'mobileCarousel'
  | 'desktopCarousel'
  | 'condensed';
export const layoutVariants = Object.keys(layout) as LayoutVariantsType[];

export default layout;
